/**
 * The pop-up that offers zevet Voice when the mic is pressed without it.
 *
 * Andrew: "if masora voice is not downloaded, you get a pop up to download
 * it." Same shape as updatedialog.tsx, and for the same reason — this is a
 * decision, made at the one moment somebody has shown they want it, which is
 * the only kind of interruption this board allows.
 *
 * The link opens outside the app: main.js's `setWindowOpenHandler` sends every
 * https target to `shell.openExternal`, so a plain anchor is the whole of it.
 * No new IPC, and no in-app browser to keep a person inside.
 *
 * There is no "don't ask again": the dialog only ever appears because someone
 * pressed the microphone, so it is already gated on intent.
 */
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useBoard } from "../lib/board";

export function VoiceDialog() {
  const voiceAsk = useBoard((s) => s.voiceAsk);
  const setVoiceAsk = useBoard((s) => s.setVoiceAsk);
  if (!voiceAsk) return null;

  return (
    <Dialog open onOpenChange={(open: boolean) => { if (!open) setVoiceAsk(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>zevet Voice isn&rsquo;t installed</DialogTitle>
          <DialogDescription>
            {/* What it IS, in one line, because "install this" with no account
                of what it does is how people decline things they wanted. */}
            zevet&rsquo;s microphone runs zevet Voice: hold a key anywhere on this
            machine and what you say is transcribed on-device and typed into
            whatever you are looking at — including this composer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setVoiceAsk(null)}>
            Not now
          </Button>
          <Button
            onClick={() => {
              window.open(voiceAsk, "_blank", "noopener");
              setVoiceAsk(null);
            }}
          >
            Get zevet Voice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What the microphone just did — only when it did NOT work.
 *
 * ⚠️ A SUCCESSFUL PRESS SAYS NOTHING AND MOVES NOTHING. This used to report
 * all four outcomes, "Listening." among them, and being a block in the
 * conversation column it pushed the chat box up every time the mic was
 * pressed. Andrew: "the flow bar can go active without altering the zevet ui,
 * just like it would with any other app" — which is also the honest model, as
 * zevet voice draws its flow bar over whatever has focus and rearranges none
 * of it.
 *
 * So the successful case is silent (lib/voice.ts § report clears the line),
 * and what is left here is the three ways a press can FAIL, rendered as an
 * overlay that occupies no space in the flow. Those still have to speak: the
 * flow bar is precisely what did not appear, and silence there would read as
 * a dead button.
 */
export function VoiceHint() {
  const line = useBoard((s) => s.voiceHotkey);
  const setVoiceHotkey = useBoard((s) => s.setVoiceHotkey);
  if (!line) return null;
  return (
    <div className="voice-hint" role="status">
      <span>{line}</span>
      <button type="button" onClick={() => setVoiceHotkey("")} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
}
